import {Alert, Box, BoxProps, Button, Typography} from '@mui/material';
import BlankCard from 'components/BlankCard/BlankCard';
import {FC, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useVacationRules} from 'hooks/useVacationRules';
import withLeavesViewModel, {
  LeavesModel,
} from 'viewComponents/LeavesBalance/leavesBalanceViewModel';

interface VacationRulesListProps extends BoxProps {
  balance?: LeavesModel;
}

const VacationRulesList: FC<VacationRulesListProps> = ({
  className,
  balance,
}) => {
  const {t} = useTranslation();
  const {data, isLoading, error, retry} = useVacationRules();

  const rules = useMemo(() => {
    const allRules = data?.rules || [];
    const yearsOfService = balance?.yearsOfService;
    if (yearsOfService === undefined) return allRules;
    const applicable = allRules.filter(rule => rule.fromYear <= yearsOfService);
    const next = allRules.find(rule => rule.fromYear > yearsOfService);
    return next ? [...applicable, next] : applicable;
  }, [data, balance?.yearsOfService]);

  const yos = balance?.yearsOfService;

  const rulesWithStatus = useMemo(
    () =>
      rules.map((rule, index) => {
        const isApplicable = yos !== undefined && rule.fromYear <= yos;
        const isCurrent =
          isApplicable &&
          yos !== undefined &&
          (index === rules.length - 1 ||
            (rules[index + 1]?.fromYear ?? Infinity) > yos);
        const isNext = yos !== undefined && rule.fromYear > yos;
        const rangeWidth = rule.toYear - rule.fromYear;
        const progressPct =
          isCurrent && rangeWidth > 0
            ? Math.min(
                100,
                Math.round(((yos! - rule.fromYear) / rangeWidth) * 100)
              )
            : isCurrent
            ? 100
            : 0;
        return {
          ...rule,
          isApplicable,
          isCurrent,
          isNext,
          rangeWidth,
          progressPct,
        };
      }),
    [rules, yos]
  );

  return (
    <Box className={`VacationRulesList ${className ?? ''}`}>
      <BlankCard className="p-0">
        <div className="px-4">
          <Typography variant="caption" color="text.secondary">
            {t(
              'leaves.VacationRulesList.subtitle',
              'Reglas de vacaciones por antigüedad en {{country}}',
              {
                country: data?.country?.name || '-',
              }
            )}
          </Typography>

          {isLoading && (
            <Typography variant="body2" className="mt-4">
              {t('leaves.VacationRulesList.loading', 'Cargando reglas...')}
            </Typography>
          )}

          {!isLoading && !!error && (
            <Alert severity="error" className="mt-4">
              <Typography variant="body2">
                {t(
                  'leaves.VacationRulesList.error',
                  'No pudimos cargar las reglas de vacaciones. Intenta de nuevo.'
                )}
              </Typography>
              <Button size="small" onClick={retry} className="mt-2">
                {t('common.retry', 'Reintentar')}
              </Button>
            </Alert>
          )}

          {!isLoading && !error && rules.length === 0 && (
            <Typography variant="body2" className="mt-4">
              {t(
                'leaves.VacationRulesList.empty',
                'No hay información de reglas disponible para tu país'
              )}
            </Typography>
          )}

          {!isLoading && !error && rules.length > 0 && (
            <div className="mt-4">
              <div
                className="flex rounded-full overflow-hidden"
                style={{height: 16, gap: 2}}
              >
                {rulesWithStatus.map(rule => (
                  <div
                    key={`bar-${rule.fromYear}`}
                    style={{
                      flex: rule.rangeWidth || 1,
                      background: rule.isCurrent
                        ? `linear-gradient(to right, #4caf50 ${rule.progressPct}%, #e0e0e0 ${rule.progressPct}%)`
                        : rule.isApplicable
                        ? '#4caf50'
                        : '#e0e0e0',
                    }}
                  />
                ))}
              </div>
              <div className="flex mt-2">
                {rulesWithStatus.map(rule => (
                  <div
                    key={`label-${rule.fromYear}`}
                    style={{flex: rule.rangeWidth || 1}}
                    className="flex flex-col items-center text-center min-w-0 px-1"
                  >
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        color: rule.isCurrent
                          ? 'success.main'
                          : 'text.secondary',
                      }}
                    >
                      {rule.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        fontWeight: 700,
                        color: rule.isCurrent
                          ? 'success.main'
                          : rule.isNext
                          ? 'primary.main'
                          : 'text.secondary',
                      }}
                    >
                      {rule.vacationDays}{' '}
                      {t('leaves.VacationRulesList.days', 'días')}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </BlankCard>
    </Box>
  );
};

export default withLeavesViewModel()(VacationRulesList);
